interface TabApi {
  show: () => void;
}

interface BootstrapGlobal {
  Tab: { getOrCreateInstance: (element: Element) => TabApi };
}

declare global {
  interface Window {
    bootstrap?: BootstrapGlobal;
  }
}

type ValidatableControl = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

const PANE_SELECTOR = '.tab-pane';

const HIDDEN_PANE_SELECTOR = `${PANE_SELECTOR}:not(.active)`;

const SERVER_ERROR_SELECTOR = `${HIDDEN_PANE_SELECTOR} .is-invalid, ${HIDDEN_PANE_SELECTOR} .invalid-feedback`;

const findTabbedForms = (): HTMLFormElement[] => Array
  .from(document.querySelectorAll<HTMLElement>(`.tab-content ${PANE_SELECTOR}`))
  .reduce<HTMLFormElement[]>((forms, pane) => {
    const form = pane.closest('form');

    return form && !forms.includes(form) ? [...forms, form] : forms;
  }, []);

const findTabTrigger = (pane: Element): HTMLElement | null => {
  const { id } = pane;

  if (!id) {
    return null;
  }

  const card = pane.closest('.card') ?? document;
  const escapedId = typeof CSS?.escape === 'function' ? CSS.escape(id) : id;

  return card.querySelector<HTMLElement>(
    `[data-bs-toggle="tab"][href="#${escapedId}"], [data-bs-toggle="tab"][data-bs-target="#${escapedId}"]`,
  );
};

const activateTab = (trigger: HTMLElement): void => {
  const tabApi = window.bootstrap?.Tab;

  if (tabApi) {
    tabApi.getOrCreateInstance(trigger).show();

    return;
  }

  trigger.click();
};

// `validity` is read instead of `checkValidity()` so that no extra `invalid` event is fired.
const isInvalid = (element: Element): element is ValidatableControl => (
  element instanceof HTMLInputElement
  || element instanceof HTMLSelectElement
  || element instanceof HTMLTextAreaElement
) && element.willValidate && !element.validity.valid;

const findFirstInvalidControl = (form: HTMLFormElement): ValidatableControl | null => Array
  .from(form.elements)
  .find(isInvalid) ?? null;

const revealServerErrors = (form: HTMLFormElement): void => {
  const error = form.querySelector(SERVER_ERROR_SELECTOR);
  const pane = error?.closest(PANE_SELECTOR);
  const trigger = pane ? findTabTrigger(pane) : null;

  if (trigger) {
    activateTab(trigger);
  }
};

/**
 * Reveals the tab that holds an invalid field.
 *
 * Tabbed CMS forms (see `cms/components/form/tabbed_card_wrapper.html.twig`) put every tab pane
 * inside a single form, and inactive panes are `display: none`. Browsers refuse to focus a hidden
 * control, so saving from another tab aborts silently with no visible validation bubble, and
 * server-rendered `is-invalid` fields stay out of sight after a failed POST.
 */
export default class FormTabValidation {
  private forms: HTMLFormElement[] = [];

  /** Form being validated by the browser right now, if any. */
  private validating: HTMLFormElement | null = null;

  /** Set while `reportValidity()` re-triggers `invalid` on the control we are revealing. */
  private reporting = false;

  public init(): void {
    this.forms = findTabbedForms();

    if (this.forms.length === 0) {
      return;
    }

    // `invalid` does not bubble, so it can only be caught on the way down.
    this.forms.forEach((form) => form.addEventListener('invalid', this.handleInvalid, true));
    this.forms.forEach((form) => revealServerErrors(form));
  }

  public destroy(): void {
    this.forms.forEach((form) => form.removeEventListener('invalid', this.handleInvalid, true));
    this.forms = [];
    this.validating = null;
    this.reporting = false;
  }

  private handleInvalid = (event: Event): void => {
    if (this.reporting) {
      return;
    }

    // Browsers report the first *focusable* invalid control, which skips fields on hidden tabs.
    // Cancelling every `invalid` of this submit attempt hands the reporting over to `flush()`.
    event.preventDefault();

    if (this.validating) {
      return;
    }

    this.validating = event.currentTarget as HTMLFormElement;
    queueMicrotask(this.flush);
  };

  /** Runs once the browser finished validating the form, so reporting is not cut short. */
  private flush = (): void => {
    const form = this.validating;

    this.validating = null;

    const control = form ? findFirstInvalidControl(form) : null;

    if (!control) {
      return;
    }

    const pane = control.closest(PANE_SELECTOR);
    const trigger = pane && !pane.classList.contains('active') ? findTabTrigger(pane) : null;

    if (!trigger) {
      this.reportInvalid(control);

      return;
    }

    trigger.addEventListener('shown.bs.tab', () => this.reportInvalid(control), { once: true });
    activateTab(trigger);
  };

  private reportInvalid(control: ValidatableControl): void {
    this.reporting = true;
    control.focus();
    control.reportValidity();
    this.reporting = false;
  }
}
